import React, { useState, useEffect, useMemo, useRef } from 'react';
import { speciesAPI } from '../services/api';
import { toast } from 'react-toastify';

const PHOTO_BASE = window.location.origin;
const getPhotoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${PHOTO_BASE}${url}`;
};

export default function AdminSpecies() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null); // full row or 'new'
  const [uploadTarget, setUploadTarget] = useState(null); // species id being uploaded to
  const fileInputRef = useRef();

  const load = () => {
    setLoading(true);
    speciesAPI.list()
      .then(res => setItems(res.data.items || []))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const filtered = useMemo(() => {
    if (!q) return items;
    const s = q.toLowerCase();
    return items.filter(sp =>
      (sp.common_name && sp.common_name.toLowerCase().includes(s)) ||
      (sp.local_name && sp.local_name.toLowerCase().includes(s)) ||
      (sp.scientific_name && sp.scientific_name.toLowerCase().includes(s)) ||
      (sp.family && sp.family.toLowerCase().includes(s))
    );
  }, [items, q]);

  const handleSave = async (form) => {
    try {
      if (form.id) {
        await speciesAPI.update(form.id, form);
        toast.success('Updated');
      } else {
        await speciesAPI.create(form);
        toast.success('Created');
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this species from the catalog? (Rows in projects using this species must be removed first.)')) return;
    try {
      await speciesAPI.delete(id);
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    if (!file || !uploadTarget) return;
    const formData = new FormData();
    formData.append('image', file);
    try {
      await speciesAPI.uploadImage(uploadTarget, formData);
      toast.success('Image uploaded');
      setUploadTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    }
    event.target.value = '';
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, color: '#1b5e20' }}>🌿 Species Management</h2>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{items.length} species in catalog</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="search" placeholder="Search..." value={q} onChange={e => setQ(e.target.value)}
            style={{ padding: '10px 14px', border: '2px solid #1b5e20', borderRadius: 8, fontSize: 14, minWidth: 200 }} />
          <button onClick={() => setEditing({ common_name: '', local_name: '', scientific_name: '', family: '', description: '' })}
            style={{ padding: '10px 16px', background: '#1a7431', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
            + Add Species
          </button>
        </div>
      </div>

      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageChange} />

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading...</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', textAlign: 'left', fontSize: 11, textTransform: 'uppercase', color: '#6b7280' }}>
                <th style={{ padding: '8px 10px' }}>Image</th>
                <th style={{ padding: '8px 10px' }}>Common Name</th>
                <th style={{ padding: '8px 10px' }}>Local Name</th>
                <th style={{ padding: '8px 10px' }}>Scientific Name</th>
                <th style={{ padding: '8px 10px' }}>Family</th>
                <th style={{ padding: '8px 10px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(sp => (
                <tr key={sp.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '8px 10px' }}>
                    {sp.image_url ? (
                      <img src={getPhotoUrl(sp.image_url)} alt={sp.common_name}
                        style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }} />
                    ) : (
                      <div style={{ width: 48, height: 48, background: '#e5e7eb', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🌱</div>
                    )}
                  </td>
                  <td style={{ padding: '8px 10px', fontWeight: 600, color: '#1a1a2e' }}>{sp.common_name}</td>
                  <td style={{ padding: '8px 10px', color: '#6b7280' }}>{sp.local_name || '—'}</td>
                  <td style={{ padding: '8px 10px', fontStyle: 'italic', color: '#374151' }}>{sp.scientific_name || '—'}</td>
                  <td style={{ padding: '8px 10px', color: '#6b7280' }}>{sp.family || '—'}</td>
                  <td style={{ padding: '8px 10px' }}>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <button onClick={() => { setUploadTarget(sp.id); fileInputRef.current.click(); }}
                        style={{ padding: '4px 8px', background: '#00897b', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>
                        📷 Photo
                      </button>
                      <button onClick={() => setEditing(sp)}
                        style={{ padding: '4px 8px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>
                        Edit
                      </button>
                      <button onClick={() => handleDelete(sp.id)}
                        style={{ padding: '4px 8px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <EditDialog row={editing} onSave={handleSave} onClose={() => setEditing(null)} />}
    </div>
  );
}

function EditDialog({ row, onSave, onClose }) {
  const [form, setForm] = useState({
    id: row.id,
    common_name: row.common_name || '',
    local_name: row.local_name || '',
    scientific_name: row.scientific_name || '',
    family: row.family || '',
    description: row.description || '',
  });

  const fld = (k, label, opts = {}) => (
    <div>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }}>{label}</label>
      {opts.textarea ? (
        <textarea value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} rows={3}
          style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, fontFamily: 'inherit' }} />
      ) : (
        <input value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })}
          style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }} />
      )}
    </div>
  );

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 600, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ margin: '0 0 16px', color: '#1b5e20' }}>{row.id ? 'Edit Species' : 'New Species'}</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {fld('common_name', 'Common Name *')}
          {fld('local_name', 'Local Name (Hindi / Kannada / regional)')}
          {fld('scientific_name', 'Scientific Name (Latin binomial)')}
          {fld('family', 'Family')}
          {fld('description', 'Description', { textarea: true })}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button onClick={onClose} style={{ padding: '8px 16px', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
            <button onClick={() => onSave(form)} style={{ padding: '8px 16px', background: '#1a7431', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}
